<script lang="ts" setup>
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { computed, ref, watch } from 'vue';
import SingleStarRatingSvg from '@/assets/svg/conversations/single-star-rating-32.svg?component';
import { ErrorService } from '@/core/shared/errors/error.service';
import { CopilotScoreFeedbackSubmittedSelection } from '@/core/shared/tracking/enums';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import ScoreFeedbackAiLearningsBanner from '@/components/Shared/Feedback/ScoreFeedbackAiLearningsBanner.vue';
import ProjectService from '@/core/shared/project/project.service';

const dialogService = new DialogService();
const applicationScoreService = new ApplicationScoreService();
const projectService = new ProjectService();

const feedbackType = ref<CopilotScoreFeedbackSubmittedSelection | undefined>(undefined);
const userScore = ref<number>();
const userComments = ref<string>();
const isLoading = ref(false);
const plusOne = ref(false);

const modalProps = computed(() => dialogService.copilotScoreFeedbackModalProps);
const allowsUserScore = computed(() => {
  if (!modalProps.value.isOpen) {
    return true;
  }
  const { feedbackType } = modalProps.value;
  return feedbackType === CopilotScoreFeedbackSubmittedSelection.THUMBSDOWN;
});

const isValidForm = computed(() => {
  if (!allowsUserScore.value) {
    return true;
  }
  return userScore.value && userScore.value > 0;
});

async function submitTrackingData(): Promise<void> {
  if (!modalProps.value.isOpen) return;

  await TrackingService.trackAction(
    TrackingActionName.COPILOT_SCORE_FEEDBACK_SUBMITTED,
    {
      jobApplicantId: modalProps.value.jobApplicantId,
      copilotScore: modalProps.value.copilotScore,
      copilotBlurb: modalProps.value.copilotBlurb,
      feedback: modalProps.value.feedbackType,
      appContext: modalProps.value.appContext,
      userScore: userScore.value,
      userComments: userComments.value,
      ...(modalProps.value.copilotChipsAsDictionary ?? {}),
    },
    { throwsOnError: true },
  );
}

async function submitScoreFeedback(): Promise<void> {
  if (!modalProps.value.isOpen || !projectService.currentProject?.jobId) return;

  const payload = {
    jobId: projectService.currentProject.jobId,
    jobApplicantId: modalProps.value.jobApplicantId,
    originalScore: modalProps.value.copilotScore,
    originalSummary: modalProps.value.copilotBlurb,
    feedback: modalProps.value.feedbackType,
    userScore: userScore.value,
    comments: userComments.value,
  };

  await applicationScoreService.submitScoreFeedback(payload);
  plusOne.value = true;
}

async function submit() {
  if (!modalProps.value.isOpen) {
    return;
  }

  try {
    isLoading.value = true;

    const results = await Promise.allSettled([submitTrackingData(), submitScoreFeedback()]);

    results.forEach((result) => {
      if (result.status === 'rejected') {
        ErrorService.captureException(result.reason);
      }
    });

    plusOne.value = true;

    // allows time for the progress bar effect before closing
    setTimeout(() => {
      showInAppNotification();
      resetForm();
      closeModal();
      isLoading.value = false;
      plusOne.value = false;
    }, 2000);
  } catch (error) {
    isLoading.value = false;
    ErrorService.captureException(error);
    SnackbarService.critical('Something went wrong. Please try again');
  }
}

async function cancel() {
  if (!modalProps.value.isOpen) {
    return;
  }

  try {
    await TrackingService.trackAction(TrackingActionName.COPILOT_SCORE_FEEDBACK_SUBMITTED, {
      jobApplicantId: modalProps.value.jobApplicantId,
      copilotScore: modalProps.value.copilotScore,
      copilotBlurb: modalProps.value.copilotBlurb,
      feedback: modalProps.value.feedbackType,
      appContext: modalProps.value.appContext,
      ...(modalProps.value.copilotChipsAsDictionary ?? {}),
    });
  } catch (error) {
    ErrorService.captureException(error);
  }

  resetForm();
  closeModal();
}

function showInAppNotification() {
  SnackbarService.showSnackbar({
    text: 'Thanks for your feedback!',
    horizontalPosition: 'center',
    prependIcon: 'success',
    prependIconCss: 'text-tint-0',
  });
}

function resetForm() {
  userScore.value = undefined;
  userComments.value = undefined;
}

function closeModal() {
  // Completely reset to initial closed state to prevent state transition timing issues
  dialogService.copilotScoreFeedbackModalProps = { isOpen: false };
}

watch(
  modalProps,
  (newProps) => {
    if ('feedbackType' in newProps) {
      feedbackType.value = newProps.feedbackType;
    }
  },
  { immediate: true },
);

watch(allowsUserScore, (newAllowUserScore) => {
  if (!newAllowUserScore) {
    userScore.value = undefined;
  }
});
</script>

<template>
  <BaseModal
    :model-value="modalProps.isOpen"
    :is-submit-disabled="!isValidForm"
    :is-loading="isLoading"
    :show-cancel-button="true"
    cancel-button-text="Skip"
    title="Give feedback"
    @cancel="cancel"
    @submit="submit"
    @clear="resetForm"
  >
    <div class="mt-4 grid gap-6" v-if="modalProps.isOpen">
      <!-- Score - show for both thumbs up and thumbs down -->
      <div
        class="flex items-center justify-between rounded-md border border-tint-40 bg-tint-20 p-4"
        v-if="modalProps.feedbackType === CopilotScoreFeedbackSubmittedSelection.THUMBSDOWN"
      >
        <span>What would you rate this candidate?</span>

        <v-rating v-model="userScore" hover half-increments :length="5">
          <template #item="item">
            <div @click="item.props.onClick">
              <SingleStarRatingSvg
                :class="{
                  'mx-0.5 text-tint-80': true,
                  'text-highlight-500': item.isFilled || item.isHovered,
                }"
              />
            </div>
          </template>
        </v-rating>
      </div>

      <!-- Comments -->
      <div>
        <textarea
          v-model="userComments"
          class="w-full resize-none rounded-md border border-tint-80 px-4 py-4 text-shade-900 placeholder:text-shade-800 focus:outline-none focus:ring-2 focus:ring-highlight-500"
          placeholder="Share your feedback or ideas on Copilot..."
          rows="4"
        />
      </div>

      <!-- Feedback count banner -->
      <div v-if="projectService.currentProject?.jobId">
        <ScoreFeedbackAiLearningsBanner
          :job-id="projectService.currentProject?.jobId"
          :plus-one="plusOne"
        />
      </div>
    </div>
  </BaseModal>
</template>
